import type { ImageCarouselProps } from './ImageCarousel.types'
import { useIntl } from '@/intl'

import { Carousel } from '@/scopes/common/components/Carousel'

import { Image } from '@sunwebgroup/uds-ui/image'
import cn from 'classnames'
import { MediaServerImage } from '../MediaServerImage'
import { MediaServerLazyImage } from '../MediaServerLazyImage'
import styles from './ImageCarousel.module.css'

const IMG_SRC_WIDTH = 520
const IMG_SRCSET_WIDTHS = [IMG_SRC_WIDTH, 600, 750, 960]

export function ImageCarousel({ href, images, partialImages = false, className, onClick, ...attributes }: ImageCarouselProps) {
  const hasMultipleImages = images.length > 1
  const intl = useIntl()

  return (
    <Carousel
      {...attributes}
      className={cn(styles.imageCarousel, { [styles.partialImages]: partialImages }, className)}
      showNavigation={hasMultipleImages}
      showDots={hasMultipleImages && !partialImages}
      watchDrag={hasMultipleImages}
      controlsPosition="inside"
      loop={!partialImages}
      slideSize={partialImages ? '80%' : '100%'}
      slideSpacing={partialImages ? 'var(--primitives-space-12)' : '0px'}
    >
      {images.length < 1 && (
        <Image isEmpty alt={intl.formatMessage({ id: 'no_image' })} />
      )}
      {images.map((imageAttributes, index) => {
        const ImageComponent = index < 2 ? MediaServerImage : MediaServerLazyImage
        const imageEl = (
          <ImageComponent
            {...imageAttributes}
            widths={IMG_SRCSET_WIDTHS}
          />
        )

        if (href) {
          return (
            <a
              className={styles.slide}
              href={href}
              key={index}
              tabIndex={-1}
              aria-hidden
              target="_blank"
            >
              {imageEl}
            </a>
          )
        }

        return (
          <span
            className={cn(styles.slide, { [styles.clickable]: !!onClick })}
            key={index}
            onClick={() => onClick?.(index)}
          >
            {imageEl}
          </span>
        )
      })}
    </Carousel>
  )
}
